import html2canvas from 'html2canvas';
import JsBarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import moment from 'moment';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import api from '../../services/api-configuration';
import ukLogo from './uk.png';

// Keep track of downloads in progress to prevent duplicate clicks
const downloadsInProgress = new Set();

/**
 * Helper function to ensure fonts are loaded
 */
const loadPdfFonts = () => {
    // Load Roboto font manually for jsPDF
    const Roboto = {
        normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf'
    };
};

/**
 * Generate barcode for a value
 * @param {string} value - The value to encode in the barcode
 * @returns {string|null} Barcode image URL or null if error
 */
const generateBarcode = (value) => {
    try {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, value, {
            format: 'CODE128',
            width: 2,
            height: 50,
            displayValue: true,
            fontSize: 12,
            margin: 10,
        });
        return canvas.toDataURL('image/png', 1.0);
    } catch (error) {
        console.error('Barkod oluşturma hatası:', error);
        return null;
    }
};

/**
 * Apply additional CSS to improve text clarity
 * @param {HTMLElement} element - The HTML element to apply styles to
 */
const applyTextOptimizationStyles = (element) => {
    const style = document.createElement('style');
    style.textContent = `
        * {
            -webkit-font-smoothing: antialiased;
            text-rendering: optimizeLegibility;
        }
        table { 
            border-collapse: collapse;
        }
        td, th {
            font-size: 12px !important;
            padding: 4px !important;
            line-height: 1.4 !important;
        }
        .vehicle-table th,
        .vehicle-table td {
            font-size: 11px !important;
            border: 1px solid black !important;
        }
    `;
    element.appendChild(style);
};

/**
 * Generate QR code for a value
 * @param {string} text - The text to encode in the QR code
 * @returns {Promise<string|null>} QR code image URL or null if error
 */
const generateQR = async (text) => {
    try {
        return await QRCode.toDataURL(text, {
            width: 120, // Larger size for better quality
            margin: 1,
            errorCorrectionLevel: 'H' // Highest error correction
        });
    } catch (err) {
        console.error('QR Code generation error:', err);
        return null;
    }
};

/**
 * Get grupaj details from API
 * @param {string} grupajNo - The grupaj number to get details for
 * @returns {Promise<Object|null>} Grupaj details or null if error
 */
const getGroupageDetails = async (grupajNo) => {
    try {
        const response = await api.post('/groupageDetails', { groupage_no: grupajNo });

        // Check if response is HTML (error page)
        if (typeof response.data === 'string' && response.data.trim().toLowerCase().startsWith('<!doctype html>')) {
            console.error('Received HTML response instead of JSON data');
            toast.error('Oturum süreniz dolmuş olabilir. Lütfen sayfayı yenileyip tekrar deneyin.');
            return null;
        }

        // Check if response has the expected data structure
        if (!response.data || typeof response.data !== 'object') {
            console.error('Invalid response format:', response.data);
            toast.error('Grupaj detayları alınamadı. Lütfen tekrar deneyin.');
            return null;
        }

        return response.data;
    } catch (error) {
        console.error('Error getting grupaj details:', error);

        // Handle specific error cases
        if (error.response) {
            if (error.response.status === 401) {
                toast.error('Oturum süreniz dolmuş. Lütfen sayfayı yenileyip tekrar deneyin.');
            } else if (error.response.status === 404) {
                toast.error('Grupaj bulunamadı.');
            } else {
                toast.error('Grupaj detayları alınamadı. Lütfen tekrar deneyin.');
            }
        } else if (error.request) {
            toast.error('Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.');
        } else {
            toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
        }

        return null;
    }
};

/**
 * Mark grupaj as printed in database
 * @param {Object} params - Parameters object
 * @param {string} params.groupage_no - The grupaj number to mark as printed
 * @returns {Promise<void>}
 */
const setPrintedColumnTrue = async ({ groupage_no }) => {
    try {
        await api.post('/setPrintedColumnTrue', { groupage_no });
    } catch (error) {
        console.error('Error setting printed column:', error);
        throw error;
    }
};

/**
 * Generate and download a Grupaj PDF
 * @param {string} grupajNo - The Grupaj number to generate PDF for
 * @param {Object} rowData - Optional row data containing from_location and to_location
 * @returns {Promise<void>}
 */
const generateAndDownloadGrupajPDF = async (grupajNo, rowData = null) => {
    // Check if this grupaj is already being downloaded
    if (downloadsInProgress.has(grupajNo)) {
        toast.info('İndirme işlemi devam ediyor, lütfen bekleyin...');
        return Promise.reject(new Error('Download already in progress'));
    }

    // Add to downloads in progress
    downloadsInProgress.add(grupajNo);

    try {
        // Get grupaj details from API
        const grupajDetails = await getGroupageDetails(grupajNo);

        if (!grupajDetails) {
            toast.error('Grupaj detayları alınamadı');
            downloadsInProgress.delete(grupajNo);
            return Promise.reject(new Error('Failed to get grupaj details'));
        }

        // Use rowData when available for locations
        if (rowData) {
            if (rowData.from_location) {
                grupajDetails.from_warehouse_name = rowData.from_location;
            }
            if (rowData.to_location) {
                grupajDetails.to_warehouse_name = rowData.to_location;
            }
        }

        // Generate QR code and barcode
        const qrImage = await generateQR(grupajDetails.groupage_no);
        const barcode = generateBarcode(grupajDetails.groupage_no);

        // Convert logo to base64
        let logoBase64 = '';
        try {
            const response = await fetch(ukLogo);
            const blob = await response.blob();
            const reader = new FileReader();
            logoBase64 = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Logo yükleme hatası:', error);
        }

        // Initialize loading and unloading groups
        const loadingGroups = {};
        const unloadingGroups = {};

        // Process chassis details for grouping
        if (grupajDetails.chassis_details) {
            grupajDetails.chassis_details.forEach(vehicle => {
                const exitWarehouse = vehicle.exit_warehouse || '';
                if (!loadingGroups[exitWarehouse]) {
                    loadingGroups[exitWarehouse] = {
                        name: exitWarehouse,
                        count: 0
                    };
                }
                loadingGroups[exitWarehouse].count++;

                const key = `${vehicle.target}-${vehicle.city_name}`;
                if (!unloadingGroups[key]) {
                    unloadingGroups[key] = {
                        address: `${vehicle.target}-${vehicle.city_name} ${vehicle.address ? '- ' + vehicle.address : ''}`,
                        count: 0
                    };
                }
                unloadingGroups[key].count++;
            });
        }

        const sortedLoadingGroups = Object.values(loadingGroups).sort((a, b) =>
            a.name.localeCompare(b.name, 'tr')
        );
        const sortedUnloadingGroups = Object.values(unloadingGroups).sort((a, b) =>
            a.address.localeCompare(b.address, 'tr')
        );

        // Geçici div oluştur - HTML2Canvas için gerekli
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = '1123px'; // A4 landscape width in pixels
        tempDiv.style.backgroundColor = '#FFFFFF';
        tempDiv.style.margin = '0';
        tempDiv.style.padding = '0';
        document.body.appendChild(tempDiv);

        // HTML içeriğini hazırla - div içerisine ekle
        tempDiv.innerHTML = `
            <div id="pdf-content" style="width: 1123px; padding: 30px; font-family: 'Times New Roman', sans-serif; font-size: 11px; line-height: 1.2; background-color: white; color: #000000; margin: 0;">
                <!-- Header -->
                <div class="header" style="display: flex; justify-content: space-between; padding: 10px 20px;">
                    <div class="logo-section" style="display: flex; gap: 10px;">
                        <img src="${logoBase64}" style="width: 60px; height: 60px;">
                        <div class="company-info" style="line-height: 1.4; color: #000000;">
                            <div>info@uklojistik.com</div>
                            <div>www.uklojistik.com</div>
                            <div>+90 (216) 688 08 98</div>
                        </div>
                    </div>
                    <div class="header-right" style="display: flex; flex-direction: column; align-items: flex-end;">
                        <div class="date" style="font-size: 16px; margin-top: 5px; color: #000000;">
                            Tarih ${moment().format('DD.MM.YYYY HH:mm:ss')}
                        </div>
                    </div>
                </div>

                <!-- Location Section -->
                <div class="location-section" style="padding: 0 20px; margin-bottom: 15px;">
                    <div class="main-locations" style="display: flex; justify-content: space-between; align-items: center; font-weight: bold; font-size: 14px; line-height: 1.3; color: #000000;">
                        <div class="location">
                            ${rowData?.from_location || grupajDetails.from_warehouse_name || ''}
                            <br /> ${rowData?.to_location || grupajDetails.to_warehouse_name || ''}
                        </div>
                        <div class="barcode-section" style="margin-left: 20mm;">
                            <img src="${qrImage}" style="height: 55px;">
                        </div>
                        <div class="barcode-section">
                            <img src="${barcode}" style="height: 55px;">
                        </div>
                    </div>
                    
                    <!-- Grupaj Info -->
                    <div class="grupaj-info" style="display: grid; grid-template-columns: 2fr 1fr 1fr; margin-top: 10px; align-items: flex-end; padding: 10px;">
                        <div style="display: flex; justify-content: space-between; width: 50%;">
                            <div>
                                <div style="margin-bottom: 5px; color: #000000;"><span>Grupaj No:</span></div>
                                <div style="margin-bottom: 5px; color: #000000;"><span>İl:</span></div>
                            </div>
                            <div>
                                <div style="margin-bottom: 5px; color: #000000;"><span>${grupajDetails.groupage_no}</span></div>
                                <div style="margin-bottom: 5px; color: #000000;"><span>${grupajDetails.chassis_details?.[0]?.city_name || ''}</span></div>
                            </div>
                        </div>
                        <div style="text-align: center;">
                            <div style="margin-bottom: 5px; color: #000000;"><span>Grupaj Tarih</span></div>
                            <div style="margin-bottom: 5px; color: #000000;"><span>En Geç Tarih</span></div>
                        </div>
                        <div style="text-align: right;">
                            <div style="margin-bottom: 5px; color: #000000;">${moment(grupajDetails.create_date).format('DD.MM.YYYY HH:mm:ss')}</div>
                            <div style="margin-bottom: 5px; color: #000000;">-</div>
                        </div>
                    </div>
                </div>

                <!-- Document Title -->
                <div style="text-align: center; font-weight: bold; margin: 15px 20px; padding: 10px 0; color: #000000;">
                    Sevkiyat Detay Bilgileri - Toplam ${grupajDetails.chassis_details?.length || 0} Otomobildir.
                </div>

                <!-- Vehicle Table -->
                <table style="width: calc(100% - 40px); margin: 0 20px 15px; border-collapse: separate; border-spacing: 0; border: 0.5px solid #000000;">
                    <thead>
                        <tr>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">-</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Şase No</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Model Adi</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Renk Adı</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Yükleme Yeri</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Sevk Yeri</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Sevk İl</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Araç Park Yeri</th>
                            <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000;">Peron</th>
                            <th style="border-bottom: 0.5px solid #000000; padding: 4px; text-align: center; font-weight: normal; color: #000000; width: 80px;">Yükleme Alanı</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${grupajDetails.chassis_details?.map((vehicle, index) => {
            const isLastRow = index === (grupajDetails.chassis_details.length - 1);
            return `
                            <tr>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${index + 1}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.chassis_no || ''}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.model || ''}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.color || ''}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.exit_warehouse || ''}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.target}-${vehicle.city_name || ''}/${vehicle.district_name || ""}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.city_name || ''}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.park_address || ''}</td>
                                <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000;">${vehicle.groupage_peron || ''}</td>
                                <td style="${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 4px; text-align: center; color: #000000; width: 80px;">${vehicle.loading_dock || ''}</td>
                            </tr>
                        `}).join('')}
                    </tbody>
                </table>

                <!-- Summary Table -->
                <div class="summary-section" style="border: none;">
                    <div style="width: calc(100% - 40px); margin: 0 20px 0; text-align: center;">
                        <div style="display: flex; justify-content: space-between; margin-bottom: 0;">
                            <div style="width: 50%; text-align: center; font-weight: bold; color: #000000; padding: 8px;">Yükleme Özeti</div>
                            <div style="width: 50%; text-align: center; font-weight: bold; color: #000000; padding: 8px;">Boşaltma Özeti</div>
                        </div>
                    </div>
                    <table style="width: calc(100% - 40px); margin: 0 20px 15px; border-collapse: separate; border-spacing: 0; border: 0.5px solid #000000; table-layout: fixed;">
                        <colgroup>
                            <col style="width: 43%;">
                            <col style="width: 7%;">
                            <col style="width: 43%;">
                            <col style="width: 7%;">
                        </colgroup>
                        <thead>
                            <tr>
                                <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 8px; text-align: center; font-weight: normal; color: #000000;">Yükleme Yeri Adresi</th>
                                <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 8px; text-align: center; font-weight: normal; width: 60px; color: #000000;">ADET</th>
                                <th style="border-right: 0.5px solid #000000; border-bottom: 0.5px solid #000000; padding: 8px; text-align: center; font-weight: normal; color: #000000;">Boşaltma Yeri Adresi</th>
                                <th style="border-bottom: 0.5px solid #000000; padding: 8px; text-align: center; font-weight: normal; width: 60px; color: #000000;">ADET</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${(() => {
                const maxRows = Math.max(sortedLoadingGroups.length, sortedUnloadingGroups.length);
                return Array.from({ length: maxRows }, (_, i) => {
                    const loadingEntry = sortedLoadingGroups[i] || { name: '', count: '' };
                    const unloadingEntry = sortedUnloadingGroups[i] || { address: '', count: '' };
                    const isLastRow = i === maxRows - 1;
                    return `
                                    <tr>
                                        <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 8px; text-align: left; color: #000000; overflow: hidden; text-overflow: ellipsis;">${loadingEntry.name}</td>
                                        <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 8px; text-align: center; color: #000000;">${loadingEntry.count}</td>
                                        <td style="border-right: 0.5px solid #000000; ${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 8px; text-align: left; color: #000000; overflow: hidden; text-overflow: ellipsis;">${unloadingEntry.address}</td>
                                        <td style="${!isLastRow ? 'border-bottom: 0.5px solid #000000;' : ''} padding: 8px; text-align: center; color: #000000;">${unloadingEntry.count}</td>
                                    </tr>
                                `}).join('')
            })()}
                        </tbody>
                    </table>
                </div>

                <!-- Signature Table -->
                <table style="width: calc(100% - 40px); margin: 20px 20px; border-collapse: collapse;">
                    <tr>
                        <td width="50%">
                            <table class="inner-signature-table" style="width: 100%;">
                                <tr>
                                    <td colspan="2" style="text-align: center; font-weight: bold; padding: 8px; color: #000000;">TESLİM EDEN</td>
                                </tr>
                                <tr>
                                    <td colspan="2" style="padding: 8px; color: #000000;">Personel Adı Soyadı:</td>
                                </tr>
                                <tr>
                                    <td colspan="2" style="height: 80px;"></td>
                                </tr>
                            </table>
                        </td>
                        <td width="50%">
                            <table class="inner-signature-table" style="width: 100%;">
                                <tr>
                                    <td colspan="2" style="text-align: center; font-weight: bold; padding: 8px; color: #000000;">TESLİM ALAN</td>
                                </tr>
                                <tr>
                                    <td style="width: 120px; padding: 4px 8px; text-align: right; color: #000000;">Taşıyıcı Firma:</td>
                                    <td style="padding: 4px 8px; color: #000000;">${grupajDetails.truck_subcontractor_name || ''}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 4px 8px; text-align: right; color: #000000;">Plaka/Dorse:</td>
                                    <td style="padding: 4px 8px; color: #000000;">${grupajDetails.plate_number || ''} ${grupajDetails.trailer_plate ? '/ ' + grupajDetails.trailer_plate : ''}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 4px 8px; text-align: right; color: #000000;">Şoför:</td>
                                    <td style="padding: 4px 8px; color: #000000;">${grupajDetails.driver_name || ''}</td>
                                </tr>
                                <tr>
                                    <td colspan="2" style="height: 80px;"></td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>
            </div>
        `;

        // HTML2Canvas ile daha yüksek çözünürlükte görüntü oluştur
        const canvas = await html2canvas(tempDiv.querySelector('#pdf-content'), {
            scale: 3, // Yüksek çözünürlük
            useCORS: true,
            letterRendering: true,
            allowTaint: true,
            backgroundColor: '#FFFFFF',
            onclone: (clonedDoc) => {
                // Ensure the clone has white background
                const clonedContent = clonedDoc.querySelector('#pdf-content');
                if (clonedContent) {
                    clonedContent.style.backgroundColor = '#FFFFFF';
                }
            }
        });

        // jsPDF ile PDF oluştur
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
            compress: true
        });

        // Canvas için arka plan rengi ayarla
        const context = canvas.getContext('2d');
        const originalData = context.getImageData(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.putImageData(originalData, 0, 0);

        // Kanvası PDF'e ekle
        const imgData = canvas.toDataURL('image/jpeg', 1.0); // En yüksek kalite
        pdf.addImage(imgData, 'JPEG', 0, 0, 297, 210); // A4 landscape boyutu (mm)

        // PDF'i indir
        pdf.save(`Grupaj-${grupajDetails.groupage_no}.pdf`);

        // Temizleme işlemi
        document.body.removeChild(tempDiv);

        // Veritabanında yazdırıldı olarak işaretle
        try {
            await setPrintedColumnTrue({ groupage_no: grupajNo });
            console.log(`Grupaj ${grupajNo} marked as printed successfully`);
        } catch (printError) {
            console.error('Error marking grupaj as printed:', printError);
        }

        return Promise.resolve();
    } catch (error) {
        console.error('Error downloading grupaj:', error);
        toast.error('Grupaj indirilirken hata oluştu');
        return Promise.reject(error);
    } finally {
        // Remove from downloads in progress
        downloadsInProgress.delete(grupajNo);
    }
};

export default generateAndDownloadGrupajPDF;